<template>
<profile-layout>
  <template slot="sidebar">
    <sidebar-modal :title="sidebarTitle"
      :active.sync="isSidebarActive"
      :button="sidebarButton"
      :loading="sidebarLoading"
      v-on:action="onSidebarAction">
      <banner :active.sync="bannerActive" :title="bannerTitle" />
      <component v-bind:is="sidebarComponent" v-bind="sidebarComponentProps"></component>
    </sidebar-modal>
  </template>
  <template slot="left">
    <div v-if="currentJobseeker">
      <empty-state v-if="currentJobseeker.status === 'private'" title="Profile Private" icon="empty-state-eyes">
        <template slot="content">
          <p>{{this.$t('jobs.profilePrivateAlert')}}</p>
        </template>
      </empty-state>
      <div v-else >
        <div class="section">
          <user-details-card :user="currentJobseeker" :editable="false" />
        </div>
        <div class="section" v-if="filteredProfiles.length > 0">
          <external-profile-section
            :profiles="filteredProfiles"
            :viewmore="true"
            :user="currentJobseeker"
            @view="onViewmoreProfiles"></external-profile-section>
        </div>
        <experience-section :id="jobseekerId" />
        <div class="section" v-if="currentJobseeker.skills && currentJobseeker.skills.length > 0">
          <skills-section :skills="currentJobseeker.skills" />
        </div>
        <dropdown-modal :active.sync="refferaldelete.approve" title="Choose An Option">
          <job-removal-modal :itemindex="refferaldelete.jobid" ></job-removal-modal>
        </dropdown-modal>
      </div>
    </div>
  </template>
  <template slot="right">
    <jobseeker-card class="is-fixed" :isOther="true" :contactLoading="contactLoading"
      :profile="currentJobseeker"
      v-on:contact="contact"
      v-on:refer="refer"
      v-on:view="onViewJobseeker"
       />
  </template>
</profile-layout>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import ProfileLayout from '@/templates/profile-layout'
import UserDetailsCard from '@/components/profile/user-details-card'
import ExperienceSection from '@/components/jobseekers/experience-section'
import SkillsSection from '@/components/jobseekers/jobseeker-skills'
import JobseekerCard from '@/components/jobseekers/jobseeker-card'
import ReferralForm from '@/components/jobseekers/job-referral-form'
import ReferConfirmation from '@/components/jobseekers/referral-confirmation'
import SidebarModal from '@/components/sidebar-modal'
import Banner from '@/components/general/banner'
import ExternalProfileSection from '@/components/profile/profiles/profile-section-ro'
import ExternalProfileList from '@/components/profile/profiles/profile-list'
import DropdownModal from '@/components/dropdown-modal'
import JobRemovalModal from '@/components/jobseekers/job-removal-modal'
import EmptyState from '@/components/general/empty-state'
import JobseekerPreferences from '@/components/jobseekers/preferences-other'
import Conversations from '../../api/conversations'

export default {
  components: {
    ProfileLayout,
    UserDetailsCard,
    ExperienceSection,
    SkillsSection,
    JobseekerCard,
    ReferralForm,
    SidebarModal,
    ReferConfirmation,
    Banner,
    ExternalProfileSection,
    ExternalProfileList,
    DropdownModal,
    JobRemovalModal,
    EmptyState,
    JobseekerPreferences
  },
  data () {
    return {
      isSidebarActive: false,
      sidebarTitle: this.$t('jobs.referForJobs'),
      sidebarLoading: false,
      sidebarButton: this.$t('jobs.submitReferrals'),
      sidebarComponent: 'ReferralForm',
      bannerActive: false,
      bannerTitle: '',
      sidebarComponentProps: {},
      contactLoading: false
    }
  },
  computed: {
    ...mapState('jobseekers', [
      'currentJobseeker'
    ]),
    ...mapGetters('users', ['profiles']),
    ...mapState('jobseekerReferral', {
      selectedReferrals: 'selected',
      refferaldelete: 'refferaldelete'
    }),
    jobseekerId () {
      return this.$route.params.id
    },
    filteredProfiles () {
      return this.profiles.filter((p) => {
        return p.profileValue !== null && p.profileValue !== ''
      })
    }
  },
  methods: {
    ...mapActions('jobseekers', [
      'getJobseeker'
    ]),
    ...mapActions('conversations', [
      'getConversationForUser'
    ]),
    ...mapMutations('jobseekerReferral', {
      setReferralJobseeker: 'setJobseeker',
      setSelectedReferrals: 'setSelected'
    }),
    ...mapMutations('jobseekers', {
      resetCurrentJobseeker: 'resetCurrentJobseeker'
    }),
    ...mapActions('jobseekerReferral', [
      'submitReferrals'
    ]),
    onViewJobseeker () {
      this.sidebarComponentProps = {isOther: true}
      this.sidebarDestructiveButton = null
      this.sidebarComponent = 'JobseekerPreferences'
      this.sidebarTitle = this.$t('jobs.jobPreferences')
      this.sidebarButton = null
      this.isSidebarActive = true
    },
    async contact () {
      try {
        this.contactLoading = true
        this.getConversationForUser(this.currentJobseeker)
        const conversation = await Conversations.getConversationWithUser(this.currentJobseeker)
        this.contactLoading = false
        this.$router.push(`/messages/${conversation.id}`)
      } catch (e) {
        const conversation = await Conversations.sendMessageToUser({userId: this.currentJobseeker.id, content: null})
        this.contactLoading = false
        this.$router.push(`/messages/${conversation.conversationId}`)
      }
    },
    async refer () {
      this.sidebarTitle = this.$t('jobs.referForJobs')
      this.sidebarComponent = 'ReferralForm'
      this.setSelectedReferrals([])
      this.setReferralJobseeker(this.currentJobseeker.id)
      this.isSidebarActive = true
      this.sidebarButton = this.$t('jobs.submitReferrals')
    },
    onViewmoreProfiles () {
      this.sidebarTitle = this.$t('jobs.socialProfiles')
      this.sidebarComponent = 'ExternalProfileList'
      this.isSidebarActive = true
      this.sidebarButton = null
    },
    async onSidebarAction () {
      if (this.selectedReferrals.length === 0) { return }
      this.$ga.event('Jobseeker', 'Referral Clicked')
      this.sidebarLoading = true
      try {
        await this.submitReferrals()
        this.sidebarTitle = this.$t('jobs.referralSuccessful')
        this.sidebarComponent = 'ReferConfirmation'
        this.sidebarButton = null
        this.bannerTitle = this.$t('jobs.referralsSubmitted')
        this.bannerActive = true
      } catch (error) {
        console.log(error)
        this.sidebarActive = false
      }
      this.sidebarLoading = false
    }
  },
  async mounted () {
    this.getJobseeker(this.jobseekerId)
    this.$store.dispatch('users/getUserProfiles', this.jobseekerId)
  },
  beforeDestroy () {
    this.resetCurrentJobseeker()
    this.sidebarComponentProps = {}
  }
}
</script>

<style lang="scss">
div.section:first-child {
  padding: 0;
}

div.section {
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-top: 2.5rem;
}

</style>



// WEBPACK FOOTER //
// src/pages/jobseekers/profile.vue